import React, { useState, useEffect } from "react";
import { SliderItem } from "../../../../types";
import { DesktopIcon } from "../../../../assets/DesktopIcon";
import { MobileIcon } from "../../../../assets/MobileIcon";
import { CarouselPreviewMedia } from "../shared/CarouselPreviewMedia";
import { CarouselPreviewCaption } from "../shared/CarouselPreviewCaption";

interface CarouselLivePreviewProps {
    items: SliderItem[];
}

export const CarouselLivePreview: React.FC<CarouselLivePreviewProps> = ({
    items
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [viewMode, setViewMode] = useState<"desktop" | "mobile">("desktop");
    const [maxHeights, setMaxHeights] = useState({ desktop: 620, mobile: 1024 });
    const isMobileView = viewMode === "mobile";

    const item = items[currentIndex];
    const brightness = item?.imageBrightness ?? 85;

    useEffect(() => {
        const calculateMaxHeights = async () => {
            let currentMaxDesktop = 0;
            let currentMaxMobile = 0;

            const promises = items.map(async (slide) => {
                const getDim = (url: string): Promise<{w: number, h: number}> => {
                    return new Promise((resolve) => {
                        const img = new Image();
                        img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
                        img.onerror = () => resolve({ w: 0, h: 0 });
                        img.src = url;
                    });
                };

                if (slide.src) {
                    const dim = await getDim(slide.src);
                    if (dim.h > 0 && dim.w > 0) {
                        const h = 1920 * (dim.h / dim.w);
                        if (h > currentMaxDesktop) currentMaxDesktop = h;
                    }
                }

                const mobileSrc = slide.mobile || slide.src;
                if (mobileSrc) {
                    const dim = await getDim(mobileSrc);
                    if (dim.h > 0 && dim.w > 0) {
                        const h = 1024 * (dim.h / dim.w);
                        if (h > currentMaxMobile) currentMaxMobile = h;
                    }
                }
            });

            await Promise.all(promises);

            setMaxHeights({
                desktop: currentMaxDesktop > 0 ? currentMaxDesktop : 620,
                mobile: currentMaxMobile > 0 ? Math.max(currentMaxMobile, 1024) : 1024 * 9 / 16
            });
        };

        calculateMaxHeights();
    }, [items]);

    useEffect(() => {
        if (currentIndex >= items.length && items.length > 0) {
            setCurrentIndex(0);
        }
    }, [items.length, currentIndex]);

    const handleNext = () => {
        setCurrentIndex((prev) => (prev + 1) % items.length);
    };

    const handlePrev = () => {
        setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
    };

    if (!items.length) return null;

    const currentMaxHeight = isMobileView ? maxHeights.mobile : maxHeights.desktop;
    const currentWidth = isMobileView ? 1024 : 1920;
    const aspectRatio = `${currentWidth} / ${currentMaxHeight}`;

    return (
        <div className="live-preview-section">
            <div className={`live-preview-container large ${isMobileView ? 'mobile-padding' : ''}`}>
                <div className="preview-header">
                    <h3> LIVE PREVIEW </h3>
                    <div className="view-mode-toggle">
                        <button
                            type="button"
                            onClick={() => setViewMode("desktop")}
                            className={viewMode === "desktop" ? "active" : ""}
                        >
                            <DesktopIcon width={16} height={16} fill={viewMode === "desktop" ? "#5493DA" : "#64748b"} /> Desktop
                        </button>
                        <button
                            type="button"
                            onClick={() => setViewMode("mobile")}
                            className={viewMode === "mobile" ? "active" : ""}
                        >
                            <MobileIcon width={16} height={16} fill={viewMode === "mobile" ? "#5493DA" : "#64748b"} /> Mobile
                        </button>
                    </div>
                </div>

                <div className="carousel-preview-wrapper">
                    <div
                        id="CarouselHome"
                        className={`carousel slide ${isMobileView ? 'mobile-view' : ''}`}
                        style={{ aspectRatio }}
                    >
                        <div className="carousel-inner">
                            <div className="item active">
                                <CarouselPreviewMedia
                                    item={item}
                                    isMobileView={isMobileView}
                                    brightness={brightness}
                                />

                                {item && !item.videoUrl && (
                                    <CarouselPreviewCaption
                                        item={item}
                                        isMobileView={isMobileView}
                                    />
                                )}
                            </div>
                        </div>


                        {items.length > 1 && (
                            <>
                                <ol className="carousel-indicators" style={{ pointerEvents: 'auto', userSelect: 'none' }}>
                                    {items.map((_, idx) => (
                                        <li
                                            key={idx}
                                            className={idx === currentIndex ? "active" : ""}
                                            onClick={() => setCurrentIndex(idx)}
                                            style={{ cursor: 'pointer', outline: 'none' }}
                                        ></li>
                                    ))}
                                </ol>
                                <div
                                    className="carousel-control"
                                    onClick={handlePrev}
                                    style={{ cursor: 'pointer', pointerEvents: 'auto', outline: 'none', userSelect: 'none' }}
                                >
                                    <div className="default">
                                       <span></span>
                                    </div>
                                </div>
                                <div
                                    className="carousel-control right"
                                    onClick={handleNext}
                                    style={{ cursor: 'pointer', pointerEvents: 'auto', outline: 'none', userSelect: 'none' }}
                                >
                                    <div className="default">
                                       <span></span>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className="preview-note">
                    {items.length > 1 && (
                    <>
                        You can use the arrows and indicators to navigate between slides.
                        <br />
                        <br />
                        <strong>💡 Hint:</strong> Seeing blank space at the bottom? This happens when your carousel images have varying heights. To fix this, try uploading images that are all the exact same height.
                    </>
                    )}
                </div>
            </div>
        </div>
    );
};
